import { isNodeKind } from '@/flow/nodeRegistry';
import type { AppEdge, AppNode } from '@/flow/types';
import type { LearningFlow } from './learningTypes';

export type AfterClearAction = 'ask' | 'autoNext' | 'stay';

export type LearningProblemProgress = {
  cleared: boolean;
  attempts: number;
  hintIdsUsed: string[];
};

export type LearningProgress = {
  version: 1;
  currentLevelId: string;
  clearedLevelIds: string[];
  afterClearAction: AfterClearAction;
  problems: Record<string, LearningProblemProgress>;
};

const progressKey = (userSub: string) => `filo:learning-progress:v1:${userSub}`;
const flowKey = (userSub: string, problemId: string) => `filo:learning-flow:v1:${userSub}:${problemId}`;

export const createLearningProgress = (): LearningProgress => ({
  version: 1,
  currentLevelId: 'level-1',
  clearedLevelIds: [],
  afterClearAction: 'ask',
  problems: {},
});

const isAfterClearAction = (value: unknown): value is AfterClearAction => (
  value === 'ask' || value === 'autoNext' || value === 'stay'
);

export const loadLearningProgress = (storage: Storage, userSub: string): LearningProgress => {
  try {
    const raw = storage.getItem(progressKey(userSub));
    if (!raw) return createLearningProgress();
    const parsed = JSON.parse(raw) as Partial<LearningProgress>;
    if (parsed.version !== 1 || typeof parsed.currentLevelId !== 'string' || !isAfterClearAction(parsed.afterClearAction)) {
      return createLearningProgress();
    }
    return {
      version: 1,
      currentLevelId: parsed.currentLevelId,
      clearedLevelIds: Array.isArray(parsed.clearedLevelIds)
        ? parsed.clearedLevelIds.filter((id): id is string => typeof id === 'string')
        : [],
      afterClearAction: parsed.afterClearAction,
      problems: parsed.problems && typeof parsed.problems === 'object' ? parsed.problems : {},
    };
  } catch {
    return createLearningProgress();
  }
};

export const saveLearningProgress = (storage: Storage, userSub: string, progress: LearningProgress) => {
  storage.setItem(progressKey(userSub), JSON.stringify(progress));
};

export const selectLearningLevel = (
  progress: LearningProgress,
  levelId: string,
): LearningProgress => ({
  ...progress,
  currentLevelId: levelId,
});

export const recordProblemAttempt = (progress: LearningProgress, problemId: string): LearningProgress => ({
  ...progress,
  problems: {
    ...progress.problems,
    [problemId]: {
      cleared: progress.problems[problemId]?.cleared ?? false,
      attempts: (progress.problems[problemId]?.attempts ?? 0) + 1,
      hintIdsUsed: progress.problems[problemId]?.hintIdsUsed ?? [],
    },
  },
});

export const recordProblemCleared = (
  progress: LearningProgress,
  problemId: string,
  levelId: string,
  levelProblemIds: string[],
): LearningProgress => {
  const problems = {
    ...progress.problems,
    [problemId]: {
      cleared: true,
      attempts: Math.max(1, progress.problems[problemId]?.attempts ?? 0),
      hintIdsUsed: progress.problems[problemId]?.hintIdsUsed ?? [],
    },
  };
  const levelCleared = levelProblemIds.every((id) => problems[id]?.cleared === true);
  return {
    ...progress,
    problems,
    clearedLevelIds: levelCleared && !progress.clearedLevelIds.includes(levelId)
      ? [...progress.clearedLevelIds, levelId]
      : progress.clearedLevelIds,
  };
};

export const recordHintUsed = (progress: LearningProgress, problemId: string, hintId: string): LearningProgress => {
  const current = progress.problems[problemId] ?? { cleared: false, attempts: 0, hintIdsUsed: [] };
  return {
    ...progress,
    problems: {
      ...progress.problems,
      [problemId]: {
        ...current,
        hintIdsUsed: current.hintIdsUsed.includes(hintId) ? current.hintIdsUsed : [...current.hintIdsUsed, hintId],
      },
    },
  };
};

export const resetProblemProgress = (
  storage: Storage,
  userSub: string,
  progress: LearningProgress,
  problemId: string,
  levelId: string,
): LearningProgress => {
  const problems = { ...progress.problems };
  delete problems[problemId];
  storage.removeItem(flowKey(userSub, problemId));
  return { ...progress, clearedLevelIds: progress.clearedLevelIds.filter((id) => id !== levelId), problems };
};

export const resetAllLearningProgress = (storage: Storage, userSub: string, problemIds: string[]) => {
  storage.removeItem(progressKey(userSub));
  problemIds.forEach((problemId) => storage.removeItem(flowKey(userSub, problemId)));
  return createLearningProgress();
};

export const saveLearningFlow = (
  storage: Storage,
  userSub: string,
  problemId: string,
  nodes: AppNode[],
  edges: AppEdge[],
) => {
  const flow: LearningFlow = {
    nodes: nodes.map((node) => ({ ...node, selected: undefined, dragging: undefined })) as AppNode[],
    edges: edges.map((edge) => ({ ...edge, selected: undefined, className: undefined })),
  };
  storage.setItem(flowKey(userSub, problemId), JSON.stringify({ version: 1, ...flow }));
};

export const loadLearningFlow = (storage: Storage, userSub: string, problemId: string): LearningFlow | null => {
  try {
    const parsed = JSON.parse(storage.getItem(flowKey(userSub, problemId)) ?? 'null') as (
      LearningFlow & { version: number }
    ) | null;
    if (!parsed || parsed.version !== 1 || !Array.isArray(parsed.nodes) || !Array.isArray(parsed.edges)) return null;
    const nodes = parsed.nodes.filter((node) => node && typeof node.id === 'string' && isNodeKind(node.type));
    const ids = new Set(nodes.map(({ id }) => id));
    const edges = parsed.edges.filter((edge) => edge && ids.has(edge.source) && ids.has(edge.target));
    return { nodes, edges };
  } catch {
    return null;
  }
};